import ArchivedFilter from '@UI/Pages/Subjects/SubjectList/Filters/ArchivedFilter';
import BooleanFieldFilter from '@UI/Pages/Subjects/SubjectList/Filters/BooleanFieldFilter';
import ChoiceFieldFilter from '@UI/Pages/Subjects/SubjectList/Filters/ChoiceFieldFilter';
import SearchFilter from '@UI/Pages/Subjects/SubjectList/Filters/SearchFilter';
import subjectFiltersSignal, {
	setSubjectFilters,
} from '@UI/Pages/Subjects/SubjectList/Filters/Signals/subjectFiltersSignal';
import SortDirectionFilter from '@UI/Pages/Subjects/SubjectList/Filters/SortDirectionFilter';
import SortFieldFilter from '@UI/Pages/Subjects/SubjectList/Filters/SortFieldFilter';
import SubjectFilterView from '@UI/Pages/Subjects/SubjectList/Filters/SubjectFilterView';
import type SubjectTypeView from '@UI/Store/SubjectTypes/Views/SubjectTypeView';
import * as React from 'react';
import { Grid, GridItem } from '@verclam/grid-system';
import { useSignalValue } from '@verclam/pulsar';
import FilterPanelSidebar from '@Guidelines/Component/FilterPanel/FilterPanelSidebar';
import FilterPanelTrigger from '@Guidelines/Component/FilterPanel/FilterPanelTrigger';
import useFilterPanel from '@Guidelines/Component/FilterPanel/Hooks/useFilterPanel';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const FIELD_COLUMN_SIZE = { xl: 2, lg: 2, md: 2 } as const;

type Props = {
	readonly type: SubjectTypeView;
};

/**
 * Lays out the search and, for the filterable and sortable fields of the type, the field filters and
 * the sort; each filter reads and writes `subjectFiltersSignal` itself. A type with neither keeps the
 * search alone.
 */
const Filters: React.FC<Props> = ({ type }) => {
	const currentFilters = useSignalValue(subjectFiltersSignal);
	const { isCompact, isOpen, panelId, open, close } = useFilterPanel();

	const filterableFields = React.useMemo(
		() => type.fields.filter((field) => field.isFilterable),
		[type.fields],
	);
	const sortableFields = React.useMemo(
		() => type.fields.filter((field) => field.isSortable),
		[type.fields],
	);
	const hasFieldControls = filterableFields.length > 0 || sortableFields.length > 0;
	const hasSort = sortableFields.length > 0;

	const handleReset = React.useCallback(() => {
		setSubjectFilters(SubjectFilterView.createDefault(type.id));
	}, [type.id]);

	const renderFieldFilters = (withLabel: boolean): React.ReactNode =>
		filterableFields.map((field) => (
			<GridItem key={field.key} columnSize={withLabel ? undefined : FIELD_COLUMN_SIZE}>
				{field.nature === 'boolean' ? (
					<BooleanFieldFilter definition={field} withLabel={withLabel} />
				) : (
					<ChoiceFieldFilter definition={field} withLabel={withLabel} />
				)}
			</GridItem>
		));

	return (
		<React.Fragment>
			<Grid columns={12} columnGap={1} rowGap={1} alignItems="end">
				<GridItem columnSize={{ xl: 4, lg: 4, md: 10, sm: 9, xs: 8 }}>
					<SearchFilter />
				</GridItem>
				<GridItem columnSize={{ xl: 2, lg: 2, md: 2, sm: 3, xs: 4 }} alignSelf="center">
					<ArchivedFilter />
				</GridItem>
				<ConditionalLoader data={hasFieldControls}>
					<ConditionalLoader
						data={!isCompact}
						fallback={
							<GridItem columnSize={{ md: 2, sm: 3, xs: 4 }}>
								<FilterPanelTrigger
									panelId={panelId}
									isOpen={isOpen}
									activeCount={currentFilters.activeFilterCount}
									onClick={open}
								/>
							</GridItem>
						}
					>
						{renderFieldFilters(false)}
						<ConditionalLoader data={hasSort}>
							<GridItem columnSize={FIELD_COLUMN_SIZE}>
								<SortFieldFilter sortableFields={sortableFields} />
							</GridItem>
							<GridItem columnSize={FIELD_COLUMN_SIZE}>
								<SortDirectionFilter />
							</GridItem>
						</ConditionalLoader>
					</ConditionalLoader>
				</ConditionalLoader>
			</Grid>
			<ConditionalLoader data={hasFieldControls}>
				<FilterPanelSidebar
					panelId={panelId}
					visible={isOpen}
					activeCount={currentFilters.activeFilterCount}
					onHide={close}
					onReset={handleReset}
				>
					<Grid columns={1} rowGap={1}>
						{renderFieldFilters(true)}
						<ConditionalLoader data={hasSort}>
							<GridItem>
								<SortFieldFilter sortableFields={sortableFields} withLabel />
							</GridItem>
							<GridItem>
								<SortDirectionFilter />
							</GridItem>
						</ConditionalLoader>
					</Grid>
				</FilterPanelSidebar>
			</ConditionalLoader>
		</React.Fragment>
	);
};

export default Filters;
